import React, { useState, useRef } from 'react';
import { Upload, X, Image, Music, AlertCircle } from 'lucide-react';
import { apiService } from '../services/api';

interface FileUploadProps {
  type: 'logo' | 'audio' | 'image';
  currentUrl?: string;
  onUpload: (url: string) => void;
  onDelete?: () => void;
  className?: string;
  disabled?: boolean;
  folder?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  type,
  currentUrl,
  onUpload,
  onDelete,
  className = '',
  disabled = false,
  folder,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const allowedTypes = {
    logo: {
      extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'],
      mimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
      maxSize: 5 * 1024 * 1024, // 5MB
      icon: Image,
      label: 'Logo',
    },
    image: {
      extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'],
      mimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
      maxSize: 5 * 1024 * 1024, // 5MB
      icon: Image,
      label: 'Image',
    },
    audio: {
      extensions: ['mp3', 'wav', 'ogg', 'm4a', 'aac'],
      mimeTypes: ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/aac'],
      maxSize: 50 * 1024 * 1024, // 50MB
      icon: Music,
      label: 'Audio',
    },
  };

  const config = allowedTypes[type];
  const IconComponent = config.icon;

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > config.maxSize) {
      return `File size must be less than ${config.maxSize / (1024 * 1024)}MB`;
    }

    // Check file extension
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !config.extensions.includes(extension)) {
      return `Only ${config.extensions.join(', ')} files are allowed`;
    }

    // Check MIME type
    if (!config.mimeTypes.includes(file.type)) {
      return `Invalid file type. Please select a valid ${config.label.toLowerCase()} file.`;
    }

    return null;
  };

  const handleFileUpload = async (file: File) => {
    setError('');
    setUploading(true);

    try {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      let uploadResult;
      if (type === 'audio') {
        uploadResult = await apiService.uploadAudio(file);
      } else {
        // For logo and image types, use the new uploadImage method
        const uploadFolder = folder || (type === 'logo' ? 'logos' : 'images');
        uploadResult = await apiService.uploadImage(file, uploadFolder);
      }

      if (uploadResult.success) {
        onUpload(uploadResult.data.url);
      } else {
        setError('Upload failed. Please try again.');
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDelete = async () => {
    if (!currentUrl || !onDelete) return;

    try {
      await apiService.deleteFile(currentUrl);
      onDelete();
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete file. Please try again.');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">
        {config.label}
      </label>

      {currentUrl ? (
        // Show current file
        <div className="flex items-center justify-between p-4 border border-gray-300 rounded-md bg-gray-50">
          <div className="flex items-center space-x-3">
            <IconComponent className="h-8 w-8 text-gray-500" />
            <div>
              <p className="text-sm font-medium text-gray-900">
                {type === 'logo' ? 'Logo uploaded' : type === 'image' ? 'Image uploaded' : 'Audio uploaded'}
              </p>
              <p className="text-xs text-gray-500 truncate max-w-xs">
                {currentUrl.split('/').pop()}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <a
              href={currentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-500 text-sm"
            >
              View
            </a>
            {onDelete && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={disabled}
                className="text-red-600 hover:text-red-500 disabled:opacity-50"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      ) : (
        // Upload area
        <div
          className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragActive
              ? 'border-indigo-500 bg-indigo-50'
              : 'border-gray-300 hover:border-gray-400'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !disabled && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={config.extensions.map(ext => `.${ext}`).join(',')}
            onChange={handleFileSelect}
            className="hidden"
            disabled={disabled}
          />

          {uploading ? (
            <div className="space-y-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="text-sm text-gray-600">Uploading...</p>
            </div>
          ) : (
            <div className="space-y-2">
              <Upload className="h-8 w-8 text-gray-400 mx-auto" />
              <div>
                <p className="text-sm text-gray-600">
                  <span className="font-medium text-indigo-600">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500">
                  {config.extensions.join(', ').toUpperCase()} up to {formatFileSize(config.maxSize)}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-center space-x-2 text-sm text-red-600">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
